import { sortRowsArray } from "@tedivo/tedivo-bay-grid-pure";
import { feetToMillimeters } from "@tedivo/tedivo-pure-helpers";
const DEFAULT_TOLERANCE_MM = 60;
/**
 * Removes the containers of a bay-level that cannot be seen from one side of the ship.
 *
 * Every container is a rectangle in the side view: `lcg ± size / 2` along the ship and
 * `bottomBase .. bottomBase + height` vertically. Walking the rows from the viewer inwards, a
 * container is dropped when the containers nearer to the viewer already cover its whole rectangle.
 * A container that is only partly covered is kept: the result is returned **from the farthest row
 * to the nearest**, so drawing it in that order (painter's order) hides the covered part and shows
 * the rest, for example the AFT half of a 40 behind a FWD 20.
 *
 * Pure: it only reads `cgs` and `size` / `height`, no DOM.
 */
export function cullOccludedContainers(containers, side, options = {}) {
    const { heightInMm, tolerance = DEFAULT_TOLERANCE_MM } = options;
    // Nearest to the viewer first. sortRowsArray orders port outer → 00 → stbd outer.
    const nearestFirst = containers
        .slice()
        .sort((a, b) => (side === "port" ? sortRowsArray(a.row, b.row) : sortRowsArray(b.row, a.row)));
    const kept = [];
    for (const container of nearestFirst) {
        const rect = toRect(container, heightInMm);
        if (rect) {
            const nearerRects = kept
                .map((k) => k.rect)
                .filter((r) => r !== undefined && rectsOverlap(r, rect, tolerance));
            if (nearerRects.length > 0 && isCovered(rect, nearerRects, tolerance))
                continue;
        }
        kept.push({ container, rect });
    }
    return kept.map((k) => k.container).reverse();
}
/** Port when the criteria looks at the ship from port, starboard otherwise */
export function getSideOfRow(row) {
    const n = Number(row);
    return Number.isFinite(n) && n !== 0 && n % 2 === 0 ? "port" : "starboard";
}
function toRect(container, heightInMm) {
    var _a, _b;
    const { cgs, size, height } = container;
    const lcg = cgs === null || cgs === void 0 ? void 0 : cgs.lcg;
    if (lcg === undefined)
        return undefined;
    const halfLength = feetToMillimeters(size) * 0.5;
    const y1 = (_b = (_a = cgs.bottomBaseWithSeparation) !== null && _a !== void 0 ? _a : cgs.bottomBase) !== null && _b !== void 0 ? _b : 0;
    const h = heightInMm !== null && heightInMm !== void 0 ? heightInMm : height;
    return { x1: lcg - halfLength, x2: lcg + halfLength, y1, y2: y1 + h };
}
function rectsOverlap(a, b, tolerance) {
    return (a.x1 < b.x2 - tolerance &&
        a.x2 > b.x1 + tolerance &&
        a.y1 < b.y2 - tolerance &&
        a.y2 > b.y1 + tolerance);
}
/** True when the union of `covers` contains `rect`, ignoring gaps smaller than `tolerance` */
function isCovered(rect, covers, tolerance) {
    // Vertical strips between every x edge that falls inside the rect
    const xs = Array.from(new Set([rect.x1, rect.x2, ...covers.flatMap((c) => [c.x1, c.x2])]
        .filter((x) => x >= rect.x1 && x <= rect.x2)
        .map((x) => Math.round(x)))).sort((a, b) => a - b);
    for (let i = 0; i < xs.length - 1; i++) {
        const xa = xs[i];
        const xb = xs[i + 1];
        if (xb - xa <= tolerance)
            continue;
        // The rects spanning this strip must cover the whole height of the rect
        const yIntervals = covers
            .filter((c) => c.x1 <= xa + tolerance && c.x2 >= xb - tolerance)
            .map((c) => ({ y1: c.y1, y2: c.y2 }))
            .sort((a, b) => a.y1 - b.y1);
        let reached = rect.y1;
        for (const iv of yIntervals) {
            if (iv.y1 > reached + tolerance)
                break;
            if (iv.y2 > reached)
                reached = iv.y2;
        }
        if (reached < rect.y2 - tolerance)
            return false;
    }
    return true;
}
